import React, { useCallback, useEffect } from 'react';
import { useObjectSettings } from '../../contexts/ObjectSettingsProvider';
import { artworkFields, useUploadFormContext } from '../../contexts/UploadFormContext';
import ArtworkDropdown from '../../fluent/ArtworkDropdown';
import { Field, ProgressBar, Spinner, makeStyles } from '@fluentui/react-components';
import { ArtworkHelper } from '../../../singleton/ArtworkHelper';
import { createShortID, updateARUrl } from '../../../utils/artwork-qr-utils';
import { getMediaType } from '../../../utils/custom-utils';

const useStyles = makeStyles({
  input: {
    backgroundColor: "var(--accent1-color-hover)",
    "& select": {
      backgroundColor: "var(--accent1-color-hover)"
    },
    "& option": {
      backgroundColor: "var(--accent1-color-hover)"
    }
  },
});


export const MediumFrameContainer = ({disabled = false}) => {
  const { selectedObject } = useObjectSettings();
  const { frames, mediums, setLoadingUpload, loadingUpload } = useUploadFormContext();
  const optionFrames = frames?.length ? frames.map(frame => ({ value: frame.model, label: frame.name, ...frame })) : [];
  const fileUrl = selectedObject.el.components["gltf-model-plus"]?.model?.userData?.metadata?.imageTexture 
    || selectedObject.el.components["media-loader"]?.data.src;

  const isPublicDomainArt = selectedObject?.el?.components['artwork']?.data?.artworkInfo?.metadata?.isPublicDomainArt;


  const mediaType = selectedObject.el.components["gltf-model-plus"]?.model?.userData?.metadata?.imageTexture ? "image"
        : getMediaType(selectedObject.el.components["media-loader"]?.data.src);

  const defaultValues = selectedObject.el.components["gltf-model-plus"]?.model?.userData?.metadata || {};
  const defaultFrame = (defaultValues?.frame && frames?.length) ? frames?.find(f => f.model == defaultValues?.frame)?.name : "--";
  const defaultMedium = defaultValues?.artworkInfo?.medium?.label || defaultValues?.artworkInfo?.medium || "--";

  const optionMediums = mediums?.length
    ? mediums.map(medium => ({ value: medium.model, label: medium.name, ...medium }))
    .filter(medium => medium.name != "Digital File" && medium.name != "Custom")
    : [];
  const [selectedMedium, setSelectedMedium] = React.useState({ label: defaultMedium });
  const [selectedFrame, setSelectedFrame] = React.useState({ label: defaultFrame });

  useEffect(() => { 
      const newDefaultFrame = (defaultValues?.frame && frames?.length) 
      ? frames?.find(f => f.model == defaultValues?.frame)?.name : "--"; 
      const newDefaultMedium = defaultValues?.artworkInfo?.medium?.label || defaultValues?.artworkInfo?.medium || "--"; 
      setSelectedFrame({ label: newDefaultFrame }); setSelectedMedium({ label: newDefaultMedium }); 
  }, [defaultFrame, defaultMedium]);

  const onRecreateObject = async({frame, medium}) => {
      setLoadingUpload(true);
      const isArtwork = !!selectedObject?.el?.components?.artwork;
      let qrCodeId;
      let data = {}
      if(isArtwork) {
          const artworkInfo = selectedObject.el?.components?.artwork?.data?.artworkInfo;
          qrCodeId = await createShortID(fileUrl);
          data = {
              [artworkFields.artName]: artworkInfo?.title,
              [artworkFields.description]: artworkInfo?.description,
              [artworkFields.artworkDate]: artworkInfo?.artworkDate,
              [artworkFields.labels]: artworkInfo?.metadata?.labels,
              [artworkFields.enableARTrigger]: artworkInfo?.metadata?.enableARTrigger,
              [artworkFields.medium]: artworkInfo?.medium?.label || artworkInfo?.medium,
              [artworkFields.artistName]: artworkInfo?.author,
              [artworkFields.artistBio]: artworkInfo?.metadata?.artistBio,
              [artworkFields.artistStatement]: artworkInfo?.artistStatement,
              [artworkFields.license]: artworkInfo?.metadata?.license,
              [artworkFields.terms]: artworkInfo?.metadata?.terms,
              [artworkFields.declareRights]: artworkInfo?.metadata?.declareRights,
              [artworkFields.AIcreation]: artworkInfo?.metadata ? artworkInfo?.metadata[artworkFields.AIcreation] : "",
              [artworkFields.aiToolUsed]: artworkInfo?.metadata?.aiToolUsed,
              [artworkFields.myMediaId]: artworkInfo?.metadata?.myMediaId,
              [artworkFields.dimensions]: artworkInfo?.dimensions,
              [artworkFields.dimensionsX]: artworkInfo?.dimensionsX,
              [artworkFields.dimensionsY]: artworkInfo?.dimensionsY,
              [artworkFields.dimensionsZ]: artworkInfo?.dimensionsZ,
              [artworkFields.terms]: true,
              [artworkFields.techniqueUsed]: artworkInfo?.metadata?.techniqueUsed,
              [artworkFields.currentPhysicalWork]: artworkInfo?.metadata?.currentPhysicalWork,
              // [artworkFields.customMedium]: artworkInfo?.metadata?.customMedium,
              [artworkFields.isPublicDomainArt]: artworkInfo?.metadata?.isPublicDomainArt,
              [artworkFields.type]: artworkInfo?.metadata?.type,
          }
      }

      let glbModel = mediaType == "model" ? fileUrl : '';
      let mediumUrl = mediums.find(m => m.name === (medium?.label || medium))?.model;
      mediumUrl = (mediumUrl == "Digital File" || mediumUrl == "Custom") ? null : mediumUrl;

      const glbToUse = ( mediaType == "image" ) 
        ? (mediumUrl ? mediumUrl : null)
        : glbModel;

      const artwork = await ArtworkHelper.getInstance().generateArtworkData({
          ...data,
          mediums,
          medium,
          imageUrl: fileUrl,
          fileUrl,
          glbModel: glbToUse, 
          frameUrl: frame,
          qrCodeId,
          mediaType,
          isPublicDomainArt,
          isArtwork
      });

      if(isArtwork) { 
          //update element in QR
          await updateARUrl(artwork?.artwork_model, qrCodeId);
      }

      selectedObject?.el?.setAttribute("media-loader", { src: artwork?.artwork_model });
      setLoadingUpload(false);
  }


  const handleFrameChange = (value, option) => {
    setSelectedFrame(option);
    let medium = mediums.find(medium => medium.name === selectedMedium.label)
    if(medium) {
      medium.label = medium.name;
      medium.value = medium.model;
    }

    onRecreateObject({frame: option.value, medium});
  };

  const handleMediumChange = (value, option) => {
    setSelectedMedium(option);
    const frame = frames.find(frame => frame.name === selectedFrame.label)?.model;
    onRecreateObject({frame, medium: option});
  };

  const styles = useStyles();

  return (
    <>
      <Field label={"Frame"} orientation='horizontal'>
        <>
        <div style={{display: "flex",flexDirection: "column"}}>
          <ArtworkDropdown
            style={{flex: "auto"}}
            id={"frame"}
            value={selectedFrame.label}
            disabled={disabled || loadingUpload}
            options={optionFrames}
            className={styles.input}
            placeholder="Select a Frame"
            onChange={handleFrameChange}
          />
          {loadingUpload && 
            <ProgressBar thickness='large' />
          }
        </div>
        </>
      </Field>
      <Field label={"Medium"} orientation='horizontal'>
        <>
        <div style={{display: "flex",flexDirection: "column"}}>
          <ArtworkDropdown
            style={{flex: "auto"}}
            id={"medium"}
            value={selectedMedium.label}
            disabled={disabled || loadingUpload}
            options={optionMediums}
            className={styles.input}
            placeholder="Select a Medium"
            onChange={handleMediumChange}
          />
          {loadingUpload && 
            <ProgressBar thickness='large' />
          }
        </div>
        </>
      </Field>
    </>
  );
};
